import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginUserAction } from '../../../store/slices/authSlice';
import {
  CheckCircleIcon,
  SparklesIcon,
  ArrowRightIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ArrowLeftOnRectangleIcon,
  EnvelopeIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const OnboardingSuccess = ({ data, onLogin, plans }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get trial days from selected plan
  const selectedPlan = plans?.find(plan => plan._id === data?.planId);
  const trialDays = selectedPlan?.trialDays || 14; // Default to 14 if not found

  const handleAccessAccount = () => {
    if (onLogin) {
      onLogin(); // This will handle the login and navigation
    } else {
      // Fallback: redirect to login page
      navigate('/login');
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const features = [
    {
      icon: UserGroupIcon,
      title: 'Employee Management',
      description: 'Manage employee information, attendance, and performance'
    },
    {
      icon: BuildingOfficeIcon,
      title: 'Multi-Branch Support',
      description: 'Handle multiple office locations seamlessly'
    },
    {
      icon: ChartBarIcon,
      title: 'Analytics & Reports',
      description: 'Get insights with comprehensive reporting tools'
    }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircleIcon className="h-12 w-12 text-white" />
        </div>

        <h2 className="text-4xl font-bold text-gray-900 mb-4">
          🎉 Welcome to IAR HRMS!
        </h2>

        <p className="text-xl text-gray-600 mb-6">
          Your company account has been successfully created with a {trialDays}-day free trial. You can now access your dashboard and start exploring all HRMS features.
        </p>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-center">
            <CheckCircleIcon className="h-6 w-6 text-green-600 mr-3" />
            <span className="text-green-800 font-medium">
              Account created successfully with email: <strong>{data?.email}</strong>
            </span>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex items-center justify-center">
            <SparklesIcon className="h-6 w-6 text-blue-600 mr-3" />
            <span className="text-blue-800 font-medium">
              {trialDays}-day free trial activated - All features unlocked!
            </span>
          </div>
        </div>

        {/* Login Instructions */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6 mb-8">
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ArrowLeftOnRectangleIcon className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Ready to Explore Your HRMS?
            </h3>
            <p className="text-gray-600 mb-4">
              Your account setup is complete! You can now check your application by logging in with your credentials.
            </p>
            <div className="flex justify-center">
              <button
                onClick={handleLogin}
                className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-pink-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl animate-pulse"
              >
                <ArrowLeftOnRectangleIcon className="h-5 w-5 mr-2" />
                Login Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Company Information Summary */}
      <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/30 backdrop-blur-sm rounded-2xl border border-white/60 shadow-xl p-8 mb-8 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/20 to-indigo-200/20 rounded-full -translate-y-16 translate-x-16"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full translate-y-12 -translate-x-12"></div>

        <div className="relative z-10">
          <div className="flex items-center justify-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <BuildingOfficeIcon className="h-6 w-6 text-white" />
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">Company Information</h3>
          <p className="text-gray-600 text-center mb-8">Your company setup is complete and ready to use</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Name Card */}
            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-white/50 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                  <BuildingOfficeIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Company Name</p>
                  <p className="text-lg font-bold text-gray-900">{data?.companyName || 'Your Company'}</p>
                </div>
              </div>
            </div>

            {/* Branch Card */}
            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-white/50 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center mr-3">
                  <UserGroupIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Branch</p>
                  <p className="text-lg font-bold text-gray-900">{data?.branchName || 'Main Branch'}</p>
                </div>
              </div>
            </div>

            {/* Admin Email Card */}
            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-white/50 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                  <EnvelopeIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Admin Email</p>
                  <p className="text-lg font-bold text-gray-900 break-all">{data?.email || 'admin@company.com'}</p>
                </div>
              </div>
            </div>

            {/* Admin Name Card */}
            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-white/50 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg flex items-center justify-center mr-3">
                  <UserIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Admin Name</p>
                  <p className="text-lg font-bold text-gray-900">{data?.firstName && data?.lastName ? `${data.firstName} ${data.lastName}` : 'Admin User'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600 mb-1">{trialDays}</div>
              <div className="text-sm text-blue-700 font-medium">Trial Days</div>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600 mb-1">{selectedPlan?.name || 'Premium'}</div>
              <div className="text-sm text-green-700 font-medium">Plan</div>
            </div>
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-600 mb-1">{data?.countryCode || 'IN'}</div>
              <div className="text-sm text-purple-700 font-medium">Country</div>
            </div>
          </div>
        </div>
      </div>

      {/* What's Next */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg p-8 mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          What's Next?
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <UserGroupIcon className="h-6 w-6 text-blue-600" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Add Your Team</h4>
            <p className="text-sm text-gray-600">
              Start by adding your employees and setting up their profiles
            </p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <BuildingOfficeIcon className="h-6 w-6 text-green-600" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Configure Settings</h4>
            <p className="text-sm text-gray-600">
              Customize workflows, policies, and system preferences
            </p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <ChartBarIcon className="h-6 w-6 text-purple-600" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Explore Features</h4>
            <p className="text-sm text-gray-600">
              Discover all the powerful tools available in your dashboard
            </p>
          </div>
        </div>

        <div className="text-center">
          <button
            className="inline-flex items-center px-8 py-4 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg"
            onClick={handleAccessAccount}
          >
            <ArrowLeftOnRectangleIcon className="h-5 w-5 mr-2" />
            Access Your Dashboard
          </button>

          <p className="text-sm text-gray-600 mt-3">
            Click above to access your HRMS dashboard and start exploring all features
          </p>
        </div>
      </div>

      {/* Quick Start Guide */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 mb-8">
        <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
          Quick Start Guide
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 flex-shrink-0">
                1
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Complete Your Profile</h4>
                <p className="text-sm text-gray-600">Add your personal information and preferences</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 flex-shrink-0">
                2
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Invite Team Members</h4>
                <p className="text-sm text-gray-600">Send invitations to your employees</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 flex-shrink-0">
                3
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Set Up Attendance</h4>
                <p className="text-sm text-gray-600">Configure attendance tracking and policies</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-start">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 flex-shrink-0">
                4
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Create Leave Policies</h4>
                <p className="text-sm text-gray-600">Define leave types and approval workflows</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 flex-shrink-0">
                5
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Set Up Payroll</h4>
                <p className="text-sm text-gray-600">Configure salary structures and pay cycles</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 flex-shrink-0">
                6
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Explore Reports</h4>
                <p className="text-sm text-gray-600">Generate insights and analytics</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Support */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-white/50 p-6 text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Need Help Getting Started?
        </h3>
        <p className="text-gray-600 mb-4">
          Our support team is here to help you make the most of IAR HRMS
        </p>
        <div className="flex justify-center space-x-4">
          <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200">
            innovativearinfotech@gmail.com
          </button>
          <button
            className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            onClick={handleLogin}
          >
            <ArrowLeftOnRectangleIcon className="h-5 w-5 mr-2 inline" />
            Login to Your Account
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingSuccess;
